<script setup>
// V1 Admin Intelligence Bridge (Part 4) — visual debugger over the existing
// Nexceo::Ai::RuntimeInspector snapshot, lead activity log and AI call log.
// Read-only, admin-only. No new backend logic — every section below maps to
// a field already returned by an existing endpoint.
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import leadsAPI from 'dashboard/api/nexceo/leads';
import aiRuntimeAPI from 'dashboard/api/nexceo/aiRuntime';
import aiLogsAPI from 'dashboard/api/nexceo/aiLogs';

const router = useRouter();
const { accountId, accountScopedRoute } = useAccount();

const searchQuery = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
let searchTimer = null;

const selectedLead = ref(null);
const snapshot = ref(null);
const activities = ref([]);
const logs = ref([]);
const isLoadingSnapshot = ref(false);
const loadError = ref('');
const showRawJson = ref(false);

function runSearch() {
  clearTimeout(searchTimer);
  if (!searchQuery.value.trim()) {
    searchResults.value = [];
    return;
  }
  searchTimer = setTimeout(async () => {
    isSearching.value = true;
    try {
      const { data } = await leadsAPI.get({
        search: searchQuery.value,
        per_page: 15,
      });
      searchResults.value = data.leads || data || [];
    } catch {
      searchResults.value = [];
    } finally {
      isSearching.value = false;
    }
  }, 300);
}

async function selectLead(lead) {
  selectedLead.value = lead;
  snapshot.value = null;
  activities.value = [];
  logs.value = [];
  loadError.value = '';
  isLoadingSnapshot.value = true;
  try {
    const { data } = await aiRuntimeAPI.show(lead.id);
    if (data?.error) {
      loadError.value = data.error;
    } else {
      snapshot.value = data;
    }
  } catch (e) {
    loadError.value =
      e?.response?.data?.error ?? 'Failed to load runtime data for this lead.';
  } finally {
    isLoadingSnapshot.value = false;
  }

  // Activity log and AI call log are supplementary — each fails on its own
  // without blocking the primary runtime snapshot above.
  try {
    const { data } = await leadsAPI.getActivities(lead.id);
    activities.value = data || [];
  } catch {
    activities.value = [];
  }
  try {
    const { data } = await aiLogsAPI.getLeadLogs(accountId.value, lead.id, {
      limit: 25,
    });
    logs.value = data?.logs || data || [];
  } catch {
    logs.value = [];
  }
}

// M26.12 Part 13 — Human Collaboration section data, already returned by
// the existing snapshot endpoint (Nexceo::Ai::RuntimeInspector#human_collaboration_snapshot).
const humanCollaboration = computed(
  () => snapshot.value?.human_collaboration || {}
);

const promptStats = computed(() => {
  if (!logs.value.length) return null;
  const durations = logs.value
    .map(l => l.duration_ms)
    .filter(v => typeof v === 'number');
  const tokens = logs.value
    .map(l => l.total_tokens || l.tokens)
    .filter(v => typeof v === 'number');
  return {
    count: logs.value.length,
    avgDuration: durations.length
      ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length)
      : null,
    maxDuration: durations.length ? Math.max(...durations) : null,
    totalTokens: tokens.length ? tokens.reduce((a, b) => a + b, 0) : null,
    failedCount: logs.value.filter(l => l.status === 'failed' || l.error)
      .length,
  };
});

function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString();
}

function formatKey(key) {
  return (key || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const matchedViaLabel = computed(
  () => `(${formatKey(humanCollaboration.value.matched_via)})`
);

function back() {
  router.push(accountScopedRoute('nexceo_settings'));
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <div
      class="flex items-center gap-3 px-4 h-14 bg-n-background border-b border-n-weak shrink-0"
    >
      <button
        class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
        @click="back"
      >
        <span class="i-lucide-arrow-left size-4" />
      </button>
      <span class="i-lucide-terminal-square size-4 text-woot-500 shrink-0" />
      <h1 class="text-base font-semibold text-n-slate-12">Runtime Inspector</h1>
      <span
        class="text-xs font-medium px-1.5 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-9"
      >
        Admin only
      </span>
    </div>

    <div class="flex-1 flex overflow-hidden">
      <!-- Lead search / picker -->
      <div
        class="w-72 shrink-0 border-r border-n-weak flex flex-col overflow-hidden"
      >
        <div class="p-3 border-b border-n-weak">
          <div class="relative">
            <span
              class="absolute left-2.5 top-1/2 -translate-y-1/2 i-lucide-search size-3.5 text-n-slate-7 pointer-events-none"
            />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search lead by name/phone…"
              class="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
              @input="runSearch"
            />
          </div>
        </div>
        <div class="flex-1 overflow-y-auto">
          <div v-if="isSearching" class="flex justify-center py-6">
            <span
              class="i-lucide-loader-2 size-4 animate-spin text-n-slate-7"
            />
          </div>
          <p
            v-else-if="!searchResults.length"
            class="text-xs text-n-slate-7 text-center py-6 px-4"
          >
            Search for a lead to inspect its AI runtime state.
          </p>
          <button
            v-for="lead in searchResults"
            :key="lead.id"
            class="flex flex-col w-full text-left px-3 py-2.5 border-b border-n-weak hover:bg-n-alpha-1 transition-colors"
            :class="selectedLead?.id === lead.id ? 'bg-n-alpha-2' : ''"
            @click="selectLead(lead)"
          >
            <span class="text-sm font-medium text-n-slate-12 truncate">{{
              lead.name || '(Unnamed Lead)'
            }}</span>
            <span class="text-xs text-n-slate-7 truncate">{{
              lead.project?.name || '—'
            }}</span>
          </button>
        </div>
      </div>

      <!-- Snapshot detail -->
      <div class="flex-1 overflow-y-auto">
        <div
          v-if="!selectedLead"
          class="flex-1 flex items-center justify-center h-full"
        >
          <p class="text-sm text-n-slate-7">
            Select a lead on the left to inspect its runtime state.
          </p>
        </div>

        <div v-else class="p-5 space-y-5 max-w-4xl">
          <div v-if="isLoadingSnapshot" class="flex justify-center py-10">
            <span class="i-lucide-loader-2 size-6 animate-spin text-woot-500" />
          </div>

          <p
            v-else-if="loadError"
            class="text-sm text-n-slate-8 px-3 py-4 text-center"
          >
            {{ loadError }}
          </p>

          <template v-else-if="snapshot">
            <!-- Overview -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <h2
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                Overview
              </h2>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <p class="text-n-slate-7">Current owner</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ snapshot.current_owner }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Capability</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(snapshot.current_capability) || '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Conversation state</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(snapshot.conversation_state) }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Decision stage</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(snapshot.decision_stage) || '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Relationship stage</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(snapshot.relationship_stage) }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Generated at</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatDate(snapshot.generated_at) }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Capability routing -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <h2
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                Capability Routing
              </h2>
              <div class="flex flex-wrap gap-1.5 mb-2">
                <span
                  v-for="cap in snapshot.capabilities_used"
                  :key="cap"
                  class="text-[11px] px-1.5 py-0.5 rounded"
                  :class="
                    cap === snapshot.current_capability
                      ? 'bg-woot-50 text-woot-700 dark:bg-woot-500/10 dark:text-woot-400 font-medium'
                      : 'bg-n-alpha-2 text-n-slate-8'
                  "
                >
                  {{ formatKey(cap) }}
                </span>
                <span
                  v-if="!(snapshot.capabilities_used || []).length"
                  class="text-xs text-n-slate-7"
                >
                  No capability transfers recorded.
                </span>
              </div>
            </div>

            <!-- Task queue -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <h2
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                Task Queue
              </h2>
              <p
                v-if="!(snapshot.pending_tasks || []).length"
                class="text-xs text-n-slate-7"
              >
                No pending tasks.
              </p>
              <ul v-else class="flex flex-col gap-1.5">
                <li
                  v-for="(task, idx) in snapshot.pending_tasks"
                  :key="idx"
                  class="flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg bg-n-alpha-1"
                >
                  <span class="text-n-slate-11">
                    {{ formatKey(task.task_type) }} ·
                    {{ formatKey(task.capability) }}
                  </span>
                  <span class="text-n-slate-7">
                    {{ task.status }} · {{ formatDate(task.created_at) }}
                  </span>
                </li>
              </ul>
            </div>

            <!-- Human Collaboration (M26.12 Part 13) -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <h2
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                Human Collaboration
              </h2>

              <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <p class="text-n-slate-7">Current task</p>
                  <p class="text-n-slate-11 font-medium">
                    {{
                      humanCollaboration.current_task
                        ? `${formatKey(humanCollaboration.current_task.task_type)} · ${humanCollaboration.current_task.status}`
                        : '—'
                    }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Current owner</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ humanCollaboration.current_owner || 'Unassigned' }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Capability · matched via</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(humanCollaboration.capability) || '—' }}
                    <span
                      v-if="humanCollaboration.matched_via"
                      class="text-n-slate-7"
                      >{{ matchedViaLabel }}</span
                    >
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Reminder status</p>
                  <p class="text-n-slate-11 font-medium">
                    {{
                      humanCollaboration.reminder_status?.next_reminder_due
                        ? `${humanCollaboration.reminder_status.next_reminder_due} due`
                        : 'None pending'
                    }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Call brief</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ humanCollaboration.call_brief ? 'Present' : '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Meeting brief</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ humanCollaboration.meeting_brief ? 'Present' : '—' }}
                  </p>
                </div>
              </div>

              <div
                v-if="humanCollaboration.conversation_summary"
                class="mt-3 pt-3 border-t border-n-weak text-xs"
              >
                <p class="text-n-slate-7 mb-1">Conversation Summary</p>
                <p class="text-n-slate-11">
                  {{
                    humanCollaboration.conversation_summary.latest_discussion ||
                    '—'
                  }}
                </p>
                <p class="text-n-slate-7 mt-1">
                  Relationship:
                  {{
                    formatKey(
                      humanCollaboration.conversation_summary.relationship
                    ) || '—'
                  }}
                  · Next:
                  {{
                    humanCollaboration.conversation_summary
                      .next_suggested_action || '—'
                  }}
                </p>
              </div>

              <div
                v-if="(humanCollaboration.internal_team_memory || []).length"
                class="mt-3 pt-3 border-t border-n-weak"
              >
                <p class="text-n-slate-7 text-xs mb-1">Internal Team Memory</p>
                <ul class="flex flex-col gap-1">
                  <li
                    v-for="(
                      note, idx
                    ) in humanCollaboration.internal_team_memory"
                    :key="idx"
                    class="text-xs text-n-slate-11"
                  >
                    • {{ note.content }}
                  </li>
                </ul>
              </div>

              <div
                v-if="(humanCollaboration.routing_history || []).length"
                class="mt-3 pt-3 border-t border-n-weak"
              >
                <p class="text-n-slate-7 text-xs mb-1">Routing History</p>
                <ol class="flex flex-col gap-1">
                  <li
                    v-for="(entry, idx) in humanCollaboration.routing_history"
                    :key="idx"
                    class="text-xs text-n-slate-9"
                  >
                    {{ entry.title }} — {{ formatKey(entry.capability) }} ({{
                      formatKey(entry.matched_via)
                    }}) · {{ formatDate(entry.at) }}
                  </li>
                </ol>
              </div>

              <div
                v-if="humanCollaboration.last_human_update"
                class="mt-3 pt-3 border-t border-n-weak text-xs"
              >
                <p class="text-n-slate-7 mb-1">Last Human Update</p>
                <p class="text-n-slate-11">
                  {{ humanCollaboration.last_human_update.title }} ·
                  {{ formatDate(humanCollaboration.last_human_update.at) }}
                </p>
              </div>

              <div
                v-if="(humanCollaboration.task_timeline || []).length"
                class="mt-3 pt-3 border-t border-n-weak"
              >
                <p class="text-n-slate-7 text-xs mb-1">Task Timeline</p>
                <ol class="flex flex-col gap-1.5">
                  <li
                    v-for="task in humanCollaboration.task_timeline"
                    :key="task.id"
                    class="text-xs"
                  >
                    <p class="text-n-slate-11">
                      {{ formatKey(task.task_type) }} — {{ task.status }}
                      <span v-if="task.outcome"
                        >({{ formatKey(task.outcome) }})</span
                      >
                    </p>
                    <p class="text-n-slate-7">
                      {{ task.owner || 'Unassigned' }} ·
                      {{ formatDate(task.created_at) }}
                    </p>
                  </li>
                </ol>
              </div>
            </div>

            <!-- Ownership timeline -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <h2
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                Ownership Timeline
              </h2>
              <p
                v-if="!(snapshot.ownership_history || []).length"
                class="text-xs text-n-slate-7"
              >
                No ownership transfers recorded.
              </p>
              <ol v-else class="flex flex-col gap-2">
                <li
                  v-for="(entry, idx) in snapshot.ownership_history"
                  :key="idx"
                  class="text-xs"
                >
                  <p class="text-n-slate-11">
                    {{ formatKey(entry.from) }}
                    <span
                      class="i-lucide-arrow-right size-3 inline-block mx-0.5"
                    />
                    {{ formatKey(entry.to) }}
                  </p>
                  <p class="text-n-slate-7">
                    {{ entry.reason }} · {{ formatDate(entry.at) }}
                  </p>
                </li>
              </ol>
            </div>

            <!-- Decision timeline -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <h2
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                Decision Timeline
              </h2>
              <p
                v-if="!(snapshot.decision_timeline || []).length"
                class="text-xs text-n-slate-7"
              >
                No decision stage changes recorded.
              </p>
              <ol v-else class="flex flex-col gap-2">
                <li
                  v-for="(entry, idx) in snapshot.decision_timeline"
                  :key="idx"
                  class="text-xs"
                >
                  <p class="text-n-slate-11">
                    {{ formatKey(entry.stage) }}
                    <span v-if="entry.confidence" class="text-n-slate-7">
                      ({{ Math.round(entry.confidence * 100) }}%)
                    </span>
                  </p>
                  <p class="text-n-slate-7">{{ formatDate(entry.at) }}</p>
                </li>
              </ol>
            </div>

            <!-- Knowledge retrieval -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <h2
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                Knowledge Retrieval
              </h2>
              <div class="flex flex-wrap gap-1.5">
                <span
                  v-for="cat in snapshot.knowledge_retrieved"
                  :key="cat"
                  class="text-xs px-2 py-1 rounded-full bg-n-alpha-2 text-n-slate-9"
                >
                  {{ formatKey(cat) }}
                </span>
                <span
                  v-if="!(snapshot.knowledge_retrieved || []).length"
                  class="text-xs text-n-slate-7"
                >
                  No knowledge categories retrieved for the current intent.
                </span>
              </div>
            </div>

            <!-- Conversation experience (M26.9.1) -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <h2
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                Conversation Experience
              </h2>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <p class="text-n-slate-7">Current style</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(snapshot.conversation_style) || '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Trust level</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(snapshot.trust_mode) || '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Question strategy</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(snapshot.question_strategy) || '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Consultation mode</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ snapshot.consultation_mode || '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Transition mode</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(snapshot.transition_mode) || '—' }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Sales experience (M26.9.2) -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <h2
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                Sales Experience
              </h2>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <p class="text-n-slate-7">Sales mode</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(snapshot.current_sales_mode) || '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Recommendation chain</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(snapshot.recommendation_chain) || '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Closing readiness</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(snapshot.closing_readiness) || '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Psychology mode</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(snapshot.psychology_mode) || '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Storytelling status</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(snapshot.storytelling_status) || '—' }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Relationship experience (M26.9.3) -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <h2
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                Relationship Experience
              </h2>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <p class="text-n-slate-7">Relationship stage</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(snapshot.customer_relationship_stage) || '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Customer familiarity</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ formatKey(snapshot.customer_familiarity) || '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Pending commitments</p>
                  <p class="text-n-slate-11 font-medium">
                    {{ (snapshot.pending_commitments || []).length || '—' }}
                  </p>
                </div>
              </div>
              <div
                v-if="(snapshot.conversation_recall || []).length"
                class="flex flex-wrap gap-1.5 mt-3"
              >
                <span
                  v-for="target in snapshot.conversation_recall"
                  :key="target"
                  class="text-xs px-2 py-1 rounded-full bg-n-alpha-2 text-n-slate-9"
                >
                  {{ formatKey(target) }}
                </span>
              </div>
              <div
                v-if="(snapshot.relationship_timeline || []).length"
                class="mt-3 space-y-1.5"
              >
                <div
                  v-for="(entry, index) in snapshot.relationship_timeline"
                  :key="index"
                  class="text-xs text-n-slate-9"
                >
                  {{ entry.title }} · {{ formatDate(entry.at) }}
                </div>
              </div>
            </div>

            <!-- Certification (M26.10 Part 14) -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <h2
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                Certification
              </h2>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <div
                  v-for="[label, key] in [
                    ['Memory', 'memory_ok'],
                    ['Knowledge', 'knowledge_ok'],
                    ['Conversation', 'conversation_ok'],
                    ['Sales', 'sales_ok'],
                    ['Relationship', 'relationship_ok'],
                    ['Workflow', 'workflow_ok'],
                  ]"
                  :key="key"
                  class="flex items-center gap-1.5"
                >
                  <span
                    class="size-3.5 shrink-0"
                    :class="
                      (snapshot.certification || {})[key]
                        ? 'i-lucide-check-circle-2 text-green-500'
                        : 'i-lucide-x-circle text-amber-500'
                    "
                  />
                  <p class="text-n-slate-11 font-medium">{{ label }}</p>
                </div>
              </div>
              <div
                v-if="snapshot.latest_replay_result"
                class="mt-3 pt-3 border-t border-n-weak text-xs text-n-slate-9"
              >
                Latest account certification:
                <span class="font-medium text-n-slate-11">{{
                  snapshot.latest_replay_result.certification_status
                }}</span>
                ({{ snapshot.latest_replay_result.overall_score_pct }}%)
              </div>
              <div
                v-else
                class="mt-3 pt-3 border-t border-n-weak text-xs text-n-slate-7"
              >
                No account-wide certification has been run yet.
              </div>
            </div>

            <!-- Conversation timeline (lead activities) -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <h2
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                Conversation Timeline
              </h2>
              <p v-if="!activities.length" class="text-xs text-n-slate-7">
                No activity recorded yet.
              </p>
              <ol v-else class="flex flex-col gap-2 max-h-72 overflow-y-auto">
                <li
                  v-for="activity in activities"
                  :key="activity.id"
                  class="text-xs"
                >
                  <p class="text-n-slate-11">{{ activity.title }}</p>
                  <p class="text-n-slate-7">
                    {{ formatKey(activity.activity_type) }} ·
                    {{ formatDate(activity.created_at) }}
                  </p>
                </li>
              </ol>
            </div>

            <!-- Prompt statistics & response latency -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <h2
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                Prompt Statistics &amp; Response Latency
              </h2>
              <div
                v-if="promptStats"
                class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs"
              >
                <div>
                  <p class="text-n-slate-7">AI calls (last 25)</p>
                  <p class="text-n-slate-11 font-semibold text-sm">
                    {{ promptStats.count }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Avg latency</p>
                  <p class="text-n-slate-11 font-semibold text-sm">
                    {{
                      promptStats.avgDuration
                        ? `${promptStats.avgDuration}ms`
                        : '—'
                    }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Max latency</p>
                  <p class="text-n-slate-11 font-semibold text-sm">
                    {{
                      promptStats.maxDuration
                        ? `${promptStats.maxDuration}ms`
                        : '—'
                    }}
                  </p>
                </div>
                <div>
                  <p class="text-n-slate-7">Failed calls</p>
                  <p class="text-n-slate-11 font-semibold text-sm">
                    {{ promptStats.failedCount }}
                  </p>
                </div>
              </div>
              <p v-else class="text-xs text-n-slate-7">No AI call logs yet.</p>
            </div>

            <!-- Event history -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <h2
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                Event History
              </h2>
              <p
                v-if="!(snapshot.internal_events || []).length"
                class="text-xs text-n-slate-7"
              >
                No workforce events recorded.
              </p>
              <ol v-else class="flex flex-col gap-2">
                <li
                  v-for="(event, idx) in snapshot.internal_events"
                  :key="idx"
                  class="text-xs"
                >
                  <p class="text-n-slate-11">{{ event.title }}</p>
                  <p class="text-n-slate-7">{{ formatDate(event.at) }}</p>
                </li>
              </ol>
            </div>

            <!-- Debug information (raw snapshot JSON) -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <button
                class="flex items-center gap-1.5 text-xs font-semibold text-n-slate-8 uppercase tracking-widest"
                @click="showRawJson = !showRawJson"
              >
                <span
                  class="size-3.5"
                  :class="
                    showRawJson
                      ? 'i-lucide-chevron-down'
                      : 'i-lucide-chevron-right'
                  "
                />
                Debug Information (raw snapshot)
              </button>
              <pre
                v-if="showRawJson"
                class="mt-3 text-[11px] leading-relaxed text-n-slate-9 bg-n-alpha-1 rounded-lg p-3 overflow-x-auto"
                >{{ JSON.stringify(snapshot, null, 2) }}</pre
              >
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
